import { FC } from "react";
import {
  Link,
  Stack,
  Typography,
  Collapse,
  Card,
  CardHeader,
  CardContent,
  Button,
  Box,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { useToggle } from "rooks";
import KeyboardArrowUpOutlinedIcon from "@mui/icons-material/KeyboardArrowUpOutlined";
import KeyboardArrowDownOutlinedIcon from "@mui/icons-material/KeyboardArrowDownOutlined";
import { AwsMissingPermissionsApplyButton } from "./AwsMissingPermissionsApplyButton";
import {
  MessagesDialog,
  MessagesDialogProps,
} from "../../common/MessagesDialog";
import { useGetUsersMeProvidersAwsAccountsByAccountIdMissingPermissionsQuery } from "../../../../../../../../services/cloudchipr.api";
import { useAppSelector } from "../../../../../../../../store/hooks";
import { currentAccountIdSelector } from "../../../../../../../../store/account/selectors/current-account/currentAccountIdSelector";
import { isCurrentAccountSubAccountSelector } from "../../../../../../../../store/account/selectors/current-account/isCurrentAccountSubAccountSelector";
import { currentAccountParentIdSelector } from "../../../../../../../../store/account/selectors/current-account/currentAccountParentIdSelector";
import { currentAccountProviderTypeSelector } from "../../../../../../../../store/account/selectors/current-account/currentAccountProviderTypeSelector";
import AwsPermissionsVideo from "../../../../../../../../assets/feature-videos/aws-permissions.mp4";
import { MissingPermissionsRecheck } from "../../common/MissingPermissionsRecheck";

export const AwsMissingPermissions: FC<MessagesDialogProps> = ({
  onClose,
  open,
}) => {
  const currentAccountId = useAppSelector(currentAccountIdSelector);
  const currentAccountIsSubAccount = useAppSelector(
    isCurrentAccountSubAccountSelector,
  );
  const currentAccountParentId = useAppSelector(currentAccountParentIdSelector);
  const currentAccountProviderType = useAppSelector(
    currentAccountProviderTypeSelector,
  );

  const [collapsed, toggleCollapsed] = useToggle();

  const { data, refetch } =
    useGetUsersMeProvidersAwsAccountsByAccountIdMissingPermissionsQuery(
      { accountId: currentAccountId || "" },
      { skip: !currentAccountId },
    );

  return (
    <MessagesDialog
      name="Permissions"
      width="md"
      open={open}
      onClose={onClose}
      actions={
        <Button onClick={onClose} color="tertiary">
          Close
        </Button>
      }
    >
      <Stack gap={2}>
        <Box>
          <Typography fontWeight="medium" pb={1}>
            Cloudchipr now supports new AWS services. Execute the provided
            change set on your existing CloudFormation template in the AWS Web
            Console to add permissions.
          </Typography>
          <video autoPlay loop height={210}>
            <track
              label="Aws missing permissions"
              kind="captions"
              src="../../../../../../../../assets/feature-videos/captions/aws-permissions.vtt"
            />
            <source src={AwsPermissionsVideo} type="video/mp4" />
          </video>
        </Box>

        <Card>
          <CardHeader
            onClick={toggleCollapsed}
            sx={{
              p: 3,
              px: 2,
              cursor: "pointer",
              bgcolor: "grey.100",
              "& .MuiCardHeader-action": {
                alignSelf: "center",
              },
            }}
            title={
              <Typography variant="body1" fontWeight="medium">
                Missing permissions
              </Typography>
            }
            action={
              collapsed ? (
                <KeyboardArrowUpOutlinedIcon />
              ) : (
                <KeyboardArrowDownOutlinedIcon />
              )
            }
          />

          <Collapse in={collapsed}>
            <CardContent
              sx={{
                p: 0,
                maxHeight: 100,
                overflow: "auto",
                bgcolor: "grey.100",
              }}
            >
              <ul style={{ margin: 0 }}>
                {data?.map((p) => <li key={p.name}>{p.name}</li>)}
              </ul>
            </CardContent>
          </Collapse>
        </Card>

        {currentAccountIsSubAccount ? (
          currentAccountParentId && (
            <Link
              component={RouterLink}
              to={`/${currentAccountProviderType}/${currentAccountParentId}`}
            >
              Enable it from your Management Account
            </Link>
          )
        ) : (
          <AwsMissingPermissionsApplyButton />
        )}

        <MissingPermissionsRecheck refetch={refetch} />
      </Stack>
    </MessagesDialog>
  );
};
